import React, { useState } from 'react';
import { YokaiInput } from '../../types/yokai';
import { validateYokaiInput } from '../../utils/validation';
import { logger } from '../../utils/logger';
import './YokaiForm.css';

interface YokaiFormProps {
  onSubmit: (input: YokaiInput) => void;
  isLoading: boolean;
}

// フィールド名マッピング（フォーム履歴対策用）
const FIELD_MAPPINGS: Record<string, keyof YokaiInput> = {
  'yokai-habitat-unique-field': 'habitat',
  'yokai-appearance-unique-field': 'appearance', 
  'yokai-attribute-unique-field': 'attribute',
  'yokai-rumor-unique-field': 'rumor'
};

const YokaiForm: React.FC<YokaiFormProps> = ({ onSubmit, isLoading }) => {
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);
  const [isGachaAnimating, setIsGachaAnimating] = useState(false);
  const [animatingField, setAnimatingField] = useState<string>('');
  
  // サンプルデータのパターン
  const sampleData = [
    {
      habitat: '深い森の奥',
      appearance: '赤い目、長い髪、霧を纏っている',
      attribute: '霧属性、銀色のオーラ',
      rumor: '迷子を安全な場所まで導いてくれる'
    },
    {
      habitat: '古い神社の境内',
      appearance: '白い着物を着ている、透明な体、美しい歌声',
      attribute: '精霊属性、白いオーラ',
      rumor: '願い事を聞いてくれると言われている'
    },
    {
      habitat: '山奥の洞窟',
      appearance: '巨大な体、鋭い爪、雷のような咆哮',
      attribute: '雷属性、紫色のオーラ',
      rumor: '雨を呼んで作物を潤してくれる'
    },
    {
      habitat: '古い井戸の底',
      appearance: '青白い肌、濡れた黒髪、水の匂いがする',
      attribute: '水属性、青いオーラ',
      rumor: '清らかな水を湧かせてくれる'
    },
    {
      habitat: '竹林の小道',
      appearance: '緑色の毛玉、くりくりした目、ぽんぽん跳ねる',
      attribute: '自然属性、緑色のオーラ',
      rumor: '植物を元気に育ててくれる'
    },
    {
      habitat: '夜の墓地',
      appearance: '青い炎を纏う、骨のような手、うめき声を上げる',
      attribute: '霊属性、紫色のオーラ',
      rumor: '迷える魂を天国に導いてくれる'
    },
    {
      habitat: '古い屋敷の屋根裏',
      appearance: '金色の毛、九本の尻尾、知恵深い瞳',
      attribute: '知恵属性、金色のオーラ',
      rumor: '困った時に良いアドバイスをくれる'
    },
    {
      habitat: '川辺の岩場',
      appearance: '甲羅を背負う、鳥のような嘴、ぬめぬめした肌',
      attribute: '水属性、青緑のオーラ',
      rumor: '川の魚たちを守ってくれる'
    },
    {
      habitat: '雪山の頂上',
      appearance: '真っ白な毛、氷のような瞳、冷たい息を吐く',
      attribute: '氷属性、水色のオーラ',
      rumor: '雪崩から登山者を救ってくれる'
    },
    {
      habitat: '古い橋の下',
      appearance: '赤い鬼の角、太い腕、ガラガラ声',
      attribute: '鬼属性、赤いオーラ',
      rumor: '夜道を安全に渡らせてくれる'
    }
  ];

  const [formData, setFormData] = useState<YokaiInput>({
    habitat: '',
    appearance: '',
    attribute: '',
    rumor: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // セキュリティ対策：ユニークなname属性をYokaiInputのフィールド名にマッピング
    const fieldName = FIELD_MAPPINGS[name] || name as keyof YokaiInput;
    
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const toggleFormCollapse = () => {
    setIsFormCollapsed(prev => !prev);
  };

  // タイピングエフェクト関数
  const typeText = (fieldName: string, text: string, delay: number = 0): Promise<void> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        setAnimatingField(fieldName);
        setFormData(prev => ({ ...prev, [fieldName]: '' }));
        
        let index = 0;
        const typeInterval = setInterval(() => {
          if (index <= text.length) {
            setFormData(prev => ({ ...prev, [fieldName]: text.substring(0, index) }));
            index++;
          } else {
            clearInterval(typeInterval);
            setAnimatingField('');
            resolve();
          }
        }, 50); // 50msごとに1文字追加
      }, delay);
    });
  };

  const handleRandomSample = async () => {
    // ガチャ演出開始
    setIsGachaAnimating(true);
    logger.userAction('派手なガチャ演出開始');
    
    // ランダムデータを選択
    const randomIndex = Math.floor(Math.random() * sampleData.length);
    const randomData = sampleData[randomIndex];
    
    // 最初にフォームをクリア
    setFormData({
      habitat: '',
      appearance: '',
      attribute: '',
      rumor: ''
    });
    
    // 派手な演出シーケンス
    setTimeout(async () => {
      // 順番にフィールドをタイピングエフェクトで入力
      await typeText('habitat', randomData.habitat, 0);
      await typeText('appearance', randomData.appearance, 200);
      await typeText('attribute', randomData.attribute, 200);
      if (randomData.rumor) {
        await typeText('rumor', randomData.rumor, 200);
      }
      
      // 演出完了
      setIsGachaAnimating(false);
      logger.userAction('派手なガチャ演出完了、ランダムサンプル選択', { index: randomIndex, data: randomData });
      
      // スマホ表示時は自動的にフォームを折りたたむ
      setIsFormCollapsed(true);
      
      // 少し遅延してから妖怪生成を実行
      setTimeout(() => {
        try {
          const validatedData = validateYokaiInput(randomData);
          logger.userAction('ランダムサンプル自動送信準備完了', validatedData);
          onSubmit(validatedData);
        } catch (error) {
          if (error instanceof Error) {
            alert(error.message);
            logger.warn('FORM', 'ランダムサンプル自動送信バリデーションエラー', error.message);
          } else {
            alert('ランダムデータに問題があります');
            logger.warn('FORM', 'ランダムサンプル不明なバリデーションエラー', error);
          }
        }
      }, 500);
    }, 500);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // バリデーションを実行
      const validatedData = validateYokaiInput(formData);
      logger.userAction('フォーム送信準備完了', validatedData);
      
      // ガチャ演出開始
      setIsGachaAnimating(true);
      logger.userAction('通常送信でのガチャ演出開始');
      
      // 派手な演出シーケンス（入力済みデータを再表示）
      setTimeout(async () => {
        // 一度フォームをクリア
        const currentData = { ...formData };
        setFormData({
          habitat: '',
          appearance: '',
          attribute: '',
          rumor: ''
        });
        
        // 順番にフィールドをタイピングエフェクトで再入力
        await typeText('habitat', currentData.habitat, 0);
        await typeText('appearance', currentData.appearance, 200);
        await typeText('attribute', currentData.attribute, 200);
        if (currentData.rumor?.trim()) {
          await typeText('rumor', currentData.rumor, 200);
        }
        
        // 演出完了
        setIsGachaAnimating(false);
        logger.userAction('通常送信でのガチャ演出完了');
        
        // スマホ表示時は自動的にフォームを折りたたむ
        setIsFormCollapsed(true);
        
        // 少し遅延してから妖怪生成を実行
        setTimeout(() => {
          onSubmit(validatedData);
        }, 500);
      }, 500);
      
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
        logger.warn('FORM', 'バリデーションエラー', error.message);
      } else {
        alert('入力内容に問題があります');
        logger.warn('FORM', '不明なバリデーションエラー', error);
      }
    }
  };


  return (
    <div className={`yokai-form ${isGachaAnimating ? 'gacha-animating' : ''}`}>
      <div className="form-top-section">
        <button 
          type="button" 
          onClick={handleRandomSample} 
          disabled={isLoading || isGachaAnimating} 
          className="header-sample-button"
        >
          {isGachaAnimating ? '妖怪を探索中...' : '最近見つかった妖怪を見る'}
        </button>
      </div>
      <div className="form-title">
        <div className="form-title-content">
          <h2>妖怪のうわさを入力</h2>
          <button 
            type="button" 
            onClick={toggleFormCollapse}
            className="form-toggle-button"
            aria-label={isFormCollapsed ? 'フォームを展開' : 'フォームを折りたたむ'}
          >
            {isFormCollapsed ? '▼' : '▲'}
          </button>
        </div>
      </div>
      <form onSubmit={handleSubmit} autoComplete="off">
        <div className={`form-content ${isLoading || isFormCollapsed ? 'collapsed' : ''}`}>
          <div className="form-group">
            <label htmlFor="habitat">妖怪の生息地</label>
            <input
              type="text"
              id="habitat"
              name="yokai-habitat-unique-field"
              value={formData.habitat}
              onChange={handleChange}
              maxLength={20}
              required
              placeholder="例: 六甲アイランド"
              className={animatingField === 'habitat' ? 'typing-animation' : ''}
              autoComplete="off"
            />
          </div>


          <div className="form-group">
            <label htmlFor="appearance">妖怪の見た目</label>
            <input
              type="text"
              id="appearance"
              name="yokai-appearance-unique-field"
              value={formData.appearance}
              onChange={handleChange}
              placeholder="例: 赤い目、長い髪、透明な体"
              className={animatingField === 'appearance' ? 'typing-animation' : ''}
              required
              autoComplete="off"
            />
          </div>

          <div className="form-group">
            <label htmlFor="attribute">妖怪のオーラ</label>
            <input
              type="text"
              id="attribute"
              name="yokai-attribute-unique-field"
              value={formData.attribute}
              onChange={handleChange}
              placeholder="例: 火属性、赤いオーラ"
              className={animatingField === 'attribute' ? 'typing-animation' : ''}
              required
              autoComplete="off"
            />
          </div>

          <div className="form-group">
            <label htmlFor="rumor">妖怪にまつわるウワサ</label>
            <input
              type="text"
              id="rumor"
              name="yokai-rumor-unique-field"
              value={formData.rumor}
              onChange={handleChange}
              placeholder="例: 満月の夜に現れる、見た者は幸運になる"
              className={animatingField === 'rumor' ? 'typing-animation' : ''}
              autoComplete="off"
            />
          </div>

          <div className="button-group">
            <button type="submit" disabled={isLoading || isGachaAnimating || !formData.habitat.trim() || !formData.appearance.trim() || !formData.attribute.trim()} className="submit-button">
              {isGachaAnimating ? '妖怪を探索中...' : (isLoading ? '妖怪を探しています...' : '妖怪を探す')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default YokaiForm;
